/** Extracts enemy subsystem creation and cross-wiring from GameScene into a standalone factory. */
import { EnemyManager } from '../systems/EnemyManager';
import { EnemyBuffManager } from '../systems/EnemyBuffManager';
import { BuildingSystem } from '../systems/BuildingSystem';
import { ProjectilePool } from '../systems/ProjectilePool';
import { distance } from '../utils/Math';
import type { SectorConfig } from '../systems/ConfigLoader';

/** Dependencies needed to spawn and wire the enemy subsystems. */
export interface EnemyWiringDeps {
  scene: Phaser.Scene;
  groundLayer: Phaser.Tilemaps.TilemapLayer;
  /** Player sprite — used for colliders and position lookups. Accepts any entity sprite type. */
  playerSprite: Phaser.GameObjects.GameObject & { x: number; y: number };
  buildingSystem: BuildingSystem;
  projectilePool: ProjectilePool;
  sectorConfig: SectorConfig | undefined;
  spawnX: number;
  spawnY: number;
}

/** What GameScene gets back after enemy subsystems are wired. */
export interface EnemyWiringResult {
  enemyManager: EnemyManager;
  buffManager: EnemyBuffManager;
}

/**
 * Spawn the enemy roster, wire colliders, projectile spawner, backup alert callback,
 * buff manager, ally counters, and corpse provider — returning the two handles
 * GameScene needs for its update loop and combat resolution.
 */
export function wireEnemySubsystems(deps: EnemyWiringDeps): EnemyWiringResult {
  const { scene, groundLayer, playerSprite, buildingSystem, projectilePool, sectorConfig, spawnX, spawnY } = deps;

  // Spawn enemies from sector config or default MVP layout
  const enemyManager = new EnemyManager(scene, groundLayer);
  if (sectorConfig) {
    enemyManager.populateFromSector(sectorConfig);
  } else {
    enemyManager.spawnNearPlayer(spawnX, spawnY);
  }
  enemyManager.setupColliders(playerSprite);
  buildingSystem.wireEnemyColliders(enemyManager);

  // Wire projectile spawner to all enemies (current + future spawns)
  enemyManager.setProjectileSpawner((x, y, tx, ty, cfg) => {
    projectilePool.spawn(x, y, tx, ty, cfg);
  });

  // Wire backup alert callback — spikes aggro on nearby enemies
  enemyManager.setBackupCallback((originX: number, originY: number, radius: number) => {
    for (const ally of enemyManager.all) {
      const dist = distance(originX, originY, ally.sprite.x, ally.sprite.y);
      if (dist <= radius && dist > 0) {
        ally.ai.spikeAggro(playerSprite.x, playerSprite.y);
      }
    }
  });

  // Buff manager — tracks active Officer auras for damage/speed multipliers
  const buffManager = new EnemyBuffManager();
  buffManager.setEnemies(enemyManager.all);

  // Wire ally counter for buff/backup skill preconditions
  for (const enemy of enemyManager.all) {
    enemy.ai.setAllyCounter((x, y, radius) => {
      let count = 0;
      for (const ally of enemyManager.all) {
        if (ally === enemy) continue;
        const dist = distance(x, y, ally.sprite.x, ally.sprite.y);
        if (dist <= radius) count++;
      }
      return count;
    });
  }

  // Wire corpse provider for investigation AI (current + future spawns)
  enemyManager.setCorpseProvider(() => enemyManager.activeCorpses);

  return { enemyManager, buffManager };
}
